import { Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import {
  ContentfulService,
  MarketingOrder,
  MarketingOrderContacts,
  MarketingOrderContactService,
} from '@lc/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIconButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { BrandService } from '@lc/core/lib/services/brand.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { E2eIdDirective } from '@lc/ui';
import { AvatarComponent } from '../../../../../ui/src/lib/avatar/avatar.component';
import { PhonePipe } from '../../../../../core/src/lib/pipes/phone.pipe';

@Component({
  selector: 'lc-contact-info',
  templateUrl: './contact-info.component.html',
  styleUrls: ['./contact-info.component.scss'],
  imports: [
    NgTemplateOutlet,
    MatDivider,
    MatProgressSpinner,
    AvatarComponent,
    MatIconButton,
    RouterLink,
    MatIcon,
    AsyncPipe,
    PhonePipe,
    E2eIdDirective,
  ],
})
export class ContactInfoComponent implements OnChanges, OnDestroy {
  private contactInfoService = inject(MarketingOrderContactService);
  contentfulService = inject(ContentfulService);

  private genericPhNumSub: Subscription;
  private previousOrder: MarketingOrder;

  @Input() showAgent: boolean = true;
  @Input() showCoLister: boolean = true;
  @Input() showCoordinator: boolean = true;
  @Input() showTeam: boolean = true;
  @Input() isCollapsed: boolean = false;
  @Input() isDetailExpand: boolean = false;
  @Input() marketingOrder: MarketingOrder;

  isLoading: boolean;
  contactInfo$: Observable<MarketingOrderContacts>;

  readonly brandService = inject(BrandService);
  readonly genericCoordinator = this.brandService.supportContact();

  constructor() {
    this.genericPhNumSub = toObservable(inject(BrandService).supportContact).subscribe({
      next: (val: any) => {
        this.genericCoordinator.phoneNumber = val.phoneNumber;
        this.genericCoordinator.email = val.email;
      },
      error: (error) => {
        throw new Error(error);
      },
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.marketingOrder && this.shouldReload(this.marketingOrder)) {
      this.isLoading = true;
      this.previousOrder = this.marketingOrder;
      this.contactInfo$ = this.contactInfoService
        .getContactInfo(this.marketingOrder._id)
        .pipe(tap(() => (this.isLoading = false)));
    }
  }

  /** Executes when the directive is destroeid */
  ngOnDestroy() {
    this.genericPhNumSub?.unsubscribe();
  }

  /**
   * Only reload the contact info if anything related to the agent, colist or team changed
   */
  private shouldReload(updatedMarketingOrder: MarketingOrder) {
    return (
      updatedMarketingOrder
      && (updatedMarketingOrder._id !== this.previousOrder?._id
        || updatedMarketingOrder.agentId !== this.previousOrder?.agentId
        || updatedMarketingOrder.coListAgentId !== this.previousOrder?.coListAgentId
        || updatedMarketingOrder.teamId !== this.previousOrder?.teamId
        || updatedMarketingOrder.listing?.agentType !== this.previousOrder?.listing?.agentType)
    );
  }
}
